import { useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import supabase from '../../lib/supabase'
import SafeIcon from '../../common/SafeIcon'
import Modal from '../common/Modal'
import * as FiIcons from 'react-icons/fi'

const statusOptions = [
  { value: 'all', label: 'Όλες οι καταστάσεις' },
  { value: 'active', label: 'Ενεργός' },
  { value: 'inactive', label: 'Ανενεργός' }
]

function emptyClientForm() {
  return {
    name: '',
    legal_name: '',
    email: '',
    phone: '',
    website: '',
    vat_number: '',
    address: '',
    city: '',
    country: '',
    notes: '',
    status: 'active',
    create_primary_contact: true,
    contact_full_name: '',
    contact_email: '',
    contact_phone: '',
    contact_position: ''
  }
}

function emptyContactForm() {
  return {
    full_name: '',
    email: '',
    phone: '',
    position: '',
    notes: '',
    make_primary: false,
    portal_enabled: false
  }
}

function buildPortalInviteUrl(inviteToken) {
  return `${window.location.origin}/#/portal/invite/${inviteToken}`
}

async function copyToClipboard(text) {
  try {
    if (navigator?.clipboard?.writeText) {
      await navigator.clipboard.writeText(text)
      return true
    }
  } catch (err) {
    console.warn('Clipboard API failed, using fallback', err)
  }

  try {
    const textarea = document.createElement('textarea')
    textarea.value = text
    textarea.setAttribute('readonly', '')
    textarea.style.position = 'fixed'
    textarea.style.top = '-1000px'
    textarea.style.left = '-1000px'
    document.body.appendChild(textarea)
    textarea.focus()
    textarea.select()
    const copied = document.execCommand('copy')
    document.body.removeChild(textarea)
    return copied
  } catch (err) {
    console.error('Fallback copy failed', err)
    return false
  }
}

export default function ClientsManager() {
  const navigate = useNavigate()
  const location = useLocation()

  const [clients, setClients] = useState([])
  const [loading, setLoading] = useState(true)
  const [detailsLoading, setDetailsLoading] = useState(false)
  const [portalActionLoading, setPortalActionLoading] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')

  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false)
  const [isContactModalOpen, setIsContactModalOpen] = useState(false)

  const [createForm, setCreateForm] = useState(emptyClientForm())
  const [editForm, setEditForm] = useState(emptyClientForm())
  const [contactForm, setContactForm] = useState(emptyContactForm())

  const [selectedClient, setSelectedClient] = useState(null)
  const [clientContacts, setClientContacts] = useState([])
  const [clientProjects, setClientProjects] = useState([])
  const [portalRows, setPortalRows] = useState([])
  const [clientInvites, setClientInvites] = useState([])
  const [clientSessions, setClientSessions] = useState([])
  const [overviewEditing, setOverviewEditing] = useState(false)
  const [consumedRouteClientId, setConsumedRouteClientId] = useState(false)

  useEffect(() => {
    fetchClients()
  }, [])

  useEffect(() => {
    const openClientId = location.state?.openClientId
    if (openClientId && !consumedRouteClientId) {
      setConsumedRouteClientId(true)
      openClientDetails(openClientId)
      navigate(location.pathname, { replace: true, state: {} })
    }
  }, [location, consumedRouteClientId, navigate])

  const filteredClients = useMemo(() => {
    const term = searchTerm.trim().toLowerCase()

    return clients.filter((client) => {
      const matchesStatus = statusFilter === 'all' || client.status === statusFilter
      const matchesSearch =
        !term ||
        client.name?.toLowerCase().includes(term) ||
        client.email?.toLowerCase().includes(term) ||
        client.phone?.toLowerCase().includes(term) ||
        client.primary_contact_name?.toLowerCase().includes(term) ||
        client.primary_contact_email?.toLowerCase().includes(term)

      return matchesStatus && matchesSearch
    })
  }, [clients, searchTerm, statusFilter])

  const latestInviteByAccessId = useMemo(() => {
    const map = {}

    for (const invite of clientInvites) {
      const existing = map[invite.portal_access_id]
      if (!existing) {
        map[invite.portal_access_id] = invite
        continue
      }

      const existingDate = existing.created_at ? new Date(existing.created_at).getTime() : 0
      const currentDate = invite.created_at ? new Date(invite.created_at).getTime() : 0

      if (currentDate > existingDate) {
        map[invite.portal_access_id] = invite
      }
    }

    return map
  }, [clientInvites])

  async function fetchClients() {
    try {
      setLoading(true)
      setError('')

      const { data, error: rpcError } = await supabase.rpc('codevaro_clients_list')

      if (rpcError) throw rpcError

      setClients(data || [])
    } catch (err) {
      console.error(err)
      setError('Σφάλμα κατά τη φόρτωση των πελατών.')
    } finally {
      setLoading(false)
    }
  }

  async function openClientDetails(clientId) {
    try {
      setDetailsLoading(true)
      setError('')
      setIsDetailsModalOpen(true)

      const [
        detailsRes,
        contactsRes,
        projectsRes,
        portalRes,
        invitesRes,
        sessionsRes
      ] = await Promise.all([
        supabase.rpc('codevaro_client_details', { p_client_id: clientId }),
        supabase.rpc('codevaro_client_contacts_list', { p_client_id: clientId }),
        supabase.rpc('codevaro_client_projects_list', { p_client_id: clientId }),
        supabase.rpc('codevaro_client_portal_overview', { p_client_id: clientId }),
        supabase.rpc('codevaro_client_portal_invites_admin_list', { p_client_id: clientId }),
        supabase.rpc('codevaro_client_portal_sessions_admin_list', { p_client_id: clientId })
      ])

      if (detailsRes.error) throw detailsRes.error
      if (contactsRes.error) throw contactsRes.error
      if (projectsRes.error) throw projectsRes.error
      if (portalRes.error) throw portalRes.error
      if (invitesRes.error) throw invitesRes.error
      if (sessionsRes.error) throw sessionsRes.error

      const details = detailsRes.data?.[0] || null
      setSelectedClient(details)
      setClientContacts(contactsRes.data || [])
      setClientProjects(projectsRes.data || [])
      setPortalRows(portalRes.data || [])
      setClientInvites(invitesRes.data || [])
      setClientSessions(sessionsRes.data || [])

      if (details) {
        setEditForm({
          name: details.name || '',
          legal_name: details.legal_name || '',
          email: details.email || '',
          phone: details.phone || '',
          website: details.website || '',
          vat_number: details.vat_number || '',
          address: details.address || '',
          city: details.city || '',
          country: details.country || '',
          notes: details.notes || '',
          status: details.status || 'active',
          create_primary_contact: true,
          contact_full_name: details.primary_contact_name || details.name || '',
          contact_email: details.primary_contact_email || details.email || '',
          contact_phone: details.primary_contact_phone || details.phone || '',
          contact_position: details.primary_contact_position || ''
        })
      }
    } catch (err) {
      console.error(err)
      setError('Σφάλμα κατά τη φόρτωση των στοιχείων πελάτη.')
      setIsDetailsModalOpen(false)
    } finally {
      setDetailsLoading(false)
    }
  }

  function openCreateClientModal() {
    setCreateForm(emptyClientForm())
    setIsCreateModalOpen(true)
  }

  async function handleCreateClient(e) {
    e.preventDefault()

    try {
      setError('')
      setSuccess('')

      const { data, error: rpcError } = await supabase.rpc('codevaro_client_create', {
        p_name: createForm.name,
        p_legal_name: createForm.legal_name || null,
        p_email: createForm.email || null,
        p_phone: createForm.phone || null,
        p_website: createForm.website || null,
        p_vat_number: createForm.vat_number || null,
        p_address: createForm.address || null,
        p_city: createForm.city || null,
        p_country: createForm.country || null,
        p_notes: createForm.notes || null,
        p_status: createForm.status || 'active',
        p_created_from_lead_id: null,
        p_owner_user_id: null,
        p_create_primary_contact: !!createForm.create_primary_contact,
        p_contact_full_name: createForm.contact_full_name || createForm.name || null,
        p_contact_email: createForm.contact_email || createForm.email || null,
        p_contact_phone: createForm.contact_phone || createForm.phone || null,
        p_contact_position: createForm.contact_position || null
      })

      if (rpcError) throw rpcError

      const created = data?.[0]
      await fetchClients()
      setIsCreateModalOpen(false)
      setSuccess('Ο πελάτης δημιουργήθηκε επιτυχώς.')

      if (created?.client_id) {
        await openClientDetails(created.client_id)
      }
    } catch (err) {
      console.error(err)
      setError(`Σφάλμα κατά τη δημιουργία πελάτη: ${err.message}`)
    }
  }

  async function handleUpdateClient(e) {
    e.preventDefault()

    if (!selectedClient?.id) return

    try {
      setError('')
      setSuccess('')

      const { error: rpcError } = await supabase.rpc('codevaro_client_update', {
        p_client_id: selectedClient.id,
        p_name: editForm.name,
        p_legal_name: editForm.legal_name || null,
        p_email: editForm.email || null,
        p_phone: editForm.phone || null,
        p_website: editForm.website || null,
        p_vat_number: editForm.vat_number || null,
        p_address: editForm.address || null,
        p_city: editForm.city || null,
        p_country: editForm.country || null,
        p_notes: editForm.notes || null,
        p_status: editForm.status || 'active'
      })

      if (rpcError) throw rpcError

      await fetchClients()
      await openClientDetails(selectedClient.id)
      setOverviewEditing(false)
      setSuccess('Τα στοιχεία πελάτη ενημερώθηκαν επιτυχώς.')
    } catch (err) {
      console.error(err)
      setError(`Σφάλμα κατά την ενημέρωση πελάτη: ${err.message}`)
    }
  }

  function openAddContactModal() {
    setContactForm(emptyContactForm())
    setIsContactModalOpen(true)
  }

  async function handleAddContact(e) {
    e.preventDefault()

    if (!selectedClient?.id) return

    try {
      setError('')
      setSuccess('')

      const { error: rpcError } = await supabase.rpc('codevaro_client_contact_create', {
        p_client_id: selectedClient.id,
        p_full_name: contactForm.full_name,
        p_email: contactForm.email || null,
        p_phone: contactForm.phone || null,
        p_position: contactForm.position || null,
        p_notes: contactForm.notes || null,
        p_make_primary: !!contactForm.make_primary,
        p_portal_enabled: !!contactForm.portal_enabled
      })

      if (rpcError) throw rpcError

      await fetchClients()
      await openClientDetails(selectedClient.id)
      setIsContactModalOpen(false)
      setSuccess('Η επαφή δημιουργήθηκε επιτυχώς.')
    } catch (err) {
      console.error(err)
      setError(`Σφάλμα κατά τη δημιουργία επαφής: ${err.message}`)
    }
  }

  async function handleSetPrimaryContact(contactId) {
    if (!selectedClient?.id) return

    try {
      setError('')
      setSuccess('')

      const { error: rpcError } = await supabase.rpc('codevaro_client_set_primary_contact', {
        p_client_id: selectedClient.id,
        p_contact_id: contactId
      })

      if (rpcError) throw rpcError

      await fetchClients()
      await openClientDetails(selectedClient.id)
      setSuccess('Η κύρια επαφή ενημερώθηκε επιτυχώς.')
    } catch (err) {
      console.error(err)
      setError(`Σφάλμα κατά τον ορισμό κύριας επαφής: ${err.message}`)
    }
  }

  async function handleEnablePortalAccess(row) {
    try {
      setPortalActionLoading(true)
      setError('')
      setSuccess('')

      const { error: rpcError } = await supabase.rpc('codevaro_client_portal_access_upsert', {
        p_client_id: selectedClient.id,
        p_contact_id: row.contact_id || null,
        p_email: row.contact_email || null,
        p_is_active: true,
        p_notes: null
      })

      if (rpcError) throw rpcError

      await openClientDetails(selectedClient.id)
      setSuccess('Το portal access ενεργοποιήθηκε επιτυχώς.')
    } catch (err) {
      console.error(err)
      setError(`Σφάλμα κατά την ενεργοποίηση portal access: ${err.message}`)
    } finally {
      setPortalActionLoading(false)
    }
  }

  async function handleTogglePortalAccess(accessId, nextValue) {
    try {
      setPortalActionLoading(true)
      setError('')
      setSuccess('')

      const { error: rpcError } = await supabase.rpc('codevaro_client_portal_access_set_active', {
        p_access_id: accessId,
        p_is_active: nextValue
      })

      if (rpcError) throw rpcError

      await openClientDetails(selectedClient.id)
      setSuccess('Η κατάσταση του portal access ενημερώθηκε επιτυχώς.')
    } catch (err) {
      console.error(err)
      setError(`Σφάλμα κατά την ενημέρωση portal access: ${err.message}`)
    } finally {
      setPortalActionLoading(false)
    }
  }

  async function handleCreateInvite(accessId) {
    try {
      setPortalActionLoading(true)
      setError('')
      setSuccess('')

      const { data, error: rpcError } = await supabase.rpc('codevaro_client_portal_invite_create', {
        p_portal_access_id: accessId,
        p_expires_hours: 72
      })

      if (rpcError) throw rpcError

      const invite = data?.[0]
      await openClientDetails(selectedClient.id)

      if (invite?.invite_token) {
        const fullUrl = buildPortalInviteUrl(invite.invite_token)
        const copied = await copyToClipboard(fullUrl)

        if (copied) {
          setSuccess(`Το invite δημιουργήθηκε και αντιγράφηκε:\n${fullUrl}`)
        } else {
          setSuccess(`Το invite δημιουργήθηκε:\n${fullUrl}`)
        }
      } else {
        setSuccess('Το invite δημιουργήθηκε επιτυχώς.')
      }
    } catch (err) {
      console.error(err)
      setError(`Σφάλμα δημιουργίας invite: ${err.message}`)
    } finally {
      setPortalActionLoading(false)
    }
  }

  async function handleCopyInvite(inviteToken) {
    try {
      setError('')
      setSuccess('')

      const fullUrl = buildPortalInviteUrl(inviteToken)
      const copied = await copyToClipboard(fullUrl)

      if (copied) {
        setSuccess(`Το invite link αντιγράφηκε:\n${fullUrl}`)
      } else {
        setSuccess(`Το invite link είναι:\n${fullUrl}`)
      }
    } catch (err) {
      console.error(err)
      setError(`Σφάλμα αντιγραφής invite link: ${err.message}`)
    }
  }

  async function handleRevokeSession(sessionId) {
    try {
      setPortalActionLoading(true)
      setError('')
      setSuccess('')

      const { error: rpcError } = await supabase.rpc('codevaro_client_portal_session_revoke', {
        p_session_id: sessionId
      })

      if (rpcError) throw rpcError

      await openClientDetails(selectedClient.id)
      setSuccess('Το session ανακλήθηκε επιτυχώς.')
    } catch (err) {
      console.error(err)
      setError(`Σφάλμα ανάκλησης session: ${err.message}`)
    } finally {
      setPortalActionLoading(false)
    }
  }

  function statusBadgeClasses(status) {
    if (status === 'active') return 'bg-green-100 text-green-800'
    if (status === 'inactive') return 'bg-gray-100 text-gray-800'
    return 'bg-blue-100 text-blue-800'
  }

  function portalStatusClasses(active) {
    return active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
  }

  function sessionStatusClasses(revokedAt, expiresAt) {
    if (revokedAt) return 'bg-red-100 text-red-800'
    if (expiresAt && new Date(expiresAt) < new Date()) return 'bg-yellow-100 text-yellow-800'
    return 'bg-green-100 text-green-800'
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Πελάτες</h1>
          <p className="mt-1 text-sm text-gray-600">
            CRM πελατών, επαφών, portal access και συσχετισμένων εσωτερικών έργων.
          </p>
        </div>

        <button
          onClick={openCreateClientModal}
          className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-white hover:bg-blue-700"
        >
          <SafeIcon icon={FiIcons.FiPlus} className="h-4 w-4" />
          Νέος Πελάτης
        </button>
      </div>

      {error ? (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-red-700">
          {error}
        </div>
      ) : null}

      {success ? (
        <div className="rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-green-700 whitespace-pre-wrap">
          {success}
        </div>
      ) : null}

      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <div className="md:col-span-2">
          <label className="mb-1 block text-sm font-medium text-gray-700">Αναζήτηση</label>
          <div className="relative">
            <SafeIcon
              icon={FiIcons.FiSearch}
              className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
            />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Όνομα, email, τηλέφωνο, κύρια επαφή..."
              className="w-full rounded-lg border border-gray-300 py-2 pl-10 pr-3 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
            />
          </div>
        </div>

        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700">Κατάσταση</label>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
          >
            {statusOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="rounded-xl bg-white shadow">
        {loading ? (
          <div className="flex h-48 items-center justify-center">
            <div className="h-10 w-10 animate-spin rounded-full border-b-2 border-blue-600"></div>
          </div>
        ) : filteredClients.length === 0 ? (
          <div className="p-10 text-center text-gray-500">
            <SafeIcon icon={FiIcons.FiUsers} className="mx-auto mb-3 h-12 w-12 text-gray-300" />
            <p>Δεν βρέθηκαν πελάτες.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                    Πελάτης
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                    Κύρια Επαφή
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                    Email
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                    Τηλέφωνο
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                    Projects
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                    Κατάσταση
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-semibold uppercase tracking-wider text-gray-500">
                    Ενέργειες
                  </th>
                </tr>
              </thead>

              <tbody className="divide-y divide-gray-200 bg-white">
                {filteredClients.map((client) => (
                  <tr key={client.id}>
                    <td className="px-6 py-4">
                      <div className="font-medium text-gray-900">{client.name}</div>
                    </td>

                    <td className="px-6 py-4">
                      <div className="font-medium text-gray-900">{client.primary_contact_name || '—'}</div>
                      <div className="text-sm text-gray-500">{client.primary_contact_email || '—'}</div>
                    </td>

                    <td className="px-6 py-4 text-sm text-gray-700">{client.email || '—'}</td>
                    <td className="px-6 py-4 text-sm text-gray-700">{client.phone || '—'}</td>
                    <td className="px-6 py-4 text-sm text-gray-700">{client.projects_count}</td>

                    <td className="px-6 py-4">
                      <span className={`rounded-full px-2.5 py-1 text-xs font-medium ${statusBadgeClasses(client.status)}`}>
                        {client.status || '—'}
                      </span>
                    </td>

                    <td className="px-6 py-4">
                      <div className="flex justify-end gap-2">
                        <button
                          onClick={() => openClientDetails(client.id)}
                          className="inline-flex items-center gap-1 rounded-lg bg-blue-50 px-3 py-2 text-sm text-blue-700 hover:bg-blue-100"
                        >
                          <SafeIcon icon={FiIcons.FiEye} className="h-4 w-4" />
                          Άνοιγμα
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Modal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        title="Νέος Πελάτης"
        size="lg"
      >
        <form onSubmit={handleCreateClient} className="space-y-5">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">Όνομα *</label>
              <input
                type="text"
                required
                value={createForm.name}
                onChange={(e) => setCreateForm((prev) => ({ ...prev, name: e.target.value }))}
                className="w-full rounded-lg border border-gray-300 px-3 py-2"
              />
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">Νομική Επωνυμία</label>
              <input
                type="text"
                value={createForm.legal_name}
                onChange={(e) => setCreateForm((prev) => ({ ...prev, legal_name: e.target.value }))}
                className="w-full rounded-lg border border-gray-300 px-3 py-2"
              />
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">Email</label>
              <input
                type="email"
                value={createForm.email}
                onChange={(e) => {
                  const value = e.target.value
                  setCreateForm((prev) => ({
                    ...prev,
                    email: value,
                    contact_email: prev.contact_email || value
                  }))
                }}
                className="w-full rounded-lg border border-gray-300 px-3 py-2"
              />
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">Τηλέφωνο</label>
              <input
                type="text"
                value={createForm.phone}
                onChange={(e) => {
                  const value = e.target.value
                  setCreateForm((prev) => ({
                    ...prev,
                    phone: value,
                    contact_phone: prev.contact_phone || value
                  }))
                }}
                className="w-full rounded-lg border border-gray-300 px-3 py-2"
              />
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">Website</label>
              <input
                type="text"
                value={createForm.website}
                onChange={(e) => setCreateForm((prev) => ({ ...prev, website: e.target.value }))}
                className="w-full rounded-lg border border-gray-300 px-3 py-2"
              />
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">ΑΦΜ</label>
              <input
                type="text"
                value={createForm.vat_number}
                onChange={(e) => setCreateForm((prev) => ({ ...prev, vat_number: e.target.value }))}
                className="w-full rounded-lg border border-gray-300 px-3 py-2"
              />
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">Πόλη</label>
              <input
                type="text"
                value={createForm.city}
                onChange={(e) => setCreateForm((prev) => ({ ...prev, city: e.target.value }))}
                className="w-full rounded-lg border border-gray-300 px-3 py-2"
              />
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">Χώρα</label>
              <input
                type="text"
                value={createForm.country}
                onChange={(e) => setCreateForm((prev) => ({ ...prev, country: e.target.value }))}
                className="w-full rounded-lg border border-gray-300 px-3 py-2"
              />
            </div>
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Διεύθυνση</label>
            <input
              type="text"
              value={createForm.address}
              onChange={(e) => setCreateForm((prev) => ({ ...prev, address: e.target.value }))}
              className="w-full rounded-lg border border-gray-300 px-3 py-2"
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Σημειώσεις</label>
            <textarea
              rows={3}
              value={createForm.notes}
              onChange={(e) => setCreateForm((prev) => ({ ...prev, notes: e.target.value }))}
              className="w-full rounded-lg border border-gray-300 px-3 py-2"
            />
          </div>

          <div className="rounded-xl border border-gray-200 p-4">
            <label className="mb-3 flex items-center gap-2 text-sm font-medium text-gray-700">
              <input
                type="checkbox"
                checked={createForm.create_primary_contact}
                onChange={(e) =>
                  setCreateForm((prev) => ({
                    ...prev,
                    create_primary_contact: e.target.checked
                  }))
                }
              />
              Δημιουργία κύριας επαφής
            </label>

            {createForm.create_primary_contact ? (
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Ονοματεπώνυμο Επαφής</label>
                  <input
                    type="text"
                    value={createForm.contact_full_name}
                    onChange={(e) =>
                      setCreateForm((prev) => ({ ...prev, contact_full_name: e.target.value }))
                    }
                    className="w-full rounded-lg border border-gray-300 px-3 py-2"
                  />
                </div>

                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Θέση</label>
                  <input
                    type="text"
                    value={createForm.contact_position}
                    onChange={(e) =>
                      setCreateForm((prev) => ({ ...prev, contact_position: e.target.value }))
                    }
                    className="w-full rounded-lg border border-gray-300 px-3 py-2"
                  />
                </div>

                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Email Επαφής</label>
                  <input
                    type="email"
                    value={createForm.contact_email}
                    onChange={(e) =>
                      setCreateForm((prev) => ({ ...prev, contact_email: e.target.value }))
                    }
                    className="w-full rounded-lg border border-gray-300 px-3 py-2"
                  />
                </div>

                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Τηλέφωνο Επαφής</label>
                  <input
                    type="text"
                    value={createForm.contact_phone}
                    onChange={(e) =>
                      setCreateForm((prev) => ({ ...prev, contact_phone: e.target.value }))
                    }
                    className="w-full rounded-lg border border-gray-300 px-3 py-2"
                  />
                </div>
              </div>
            ) : null}
          </div>

          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={() => setIsCreateModalOpen(false)}
              className="rounded-lg bg-gray-100 px-4 py-2 text-gray-700 hover:bg-gray-200"
            >
              Ακύρωση
            </button>

            <button
              type="submit"
              className="rounded-lg bg-blue-600 px-4 py-2 text-white hover:bg-blue-700"
            >
              Αποθήκευση
            </button>
          </div>
        </form>
      </Modal>

      <Modal
        isOpen={isDetailsModalOpen}
        onClose={() => {
          setIsDetailsModalOpen(false)
          setSelectedClient(null)
          setOverviewEditing(false)
          setPortalRows([])
          setClientInvites([])
          setClientSessions([])
        }}
        title={selectedClient ? `Πελάτης: ${selectedClient.name}` : 'Στοιχεία Πελάτη'}
        size="xl"
      >
        {detailsLoading || !selectedClient ? (
          <div className="flex h-40 items-center justify-center">
            <div className="h-10 w-10 animate-spin rounded-full border-b-2 border-blue-600"></div>
          </div>
        ) : (
          <div className="space-y-6">
            <div className="rounded-xl border border-gray-200 p-5">
              <div className="mb-4 flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Overview</h3>
                  <p className="text-sm text-gray-500">
                    Βασικά στοιχεία πελάτη και κύριας επαφής.
                  </p>
                </div>

                <button
                  onClick={() => setOverviewEditing((prev) => !prev)}
                  className="inline-flex items-center gap-2 rounded-lg bg-gray-100 px-3 py-2 text-sm text-gray-700 hover:bg-gray-200"
                >
                  <SafeIcon icon={FiIcons.FiEdit2} className="h-4 w-4" />
                  {overviewEditing ? 'Κλείσιμο Επεξεργασίας' : 'Επεξεργασία'}
                </button>
              </div>

              {overviewEditing ? (
                <form onSubmit={handleUpdateClient} className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div>
                    <label className="mb-1 block text-sm font-medium text-gray-700">Όνομα *</label>
                    <input
                      type="text"
                      required
                      value={editForm.name}
                      onChange={(e) => setEditForm((prev) => ({ ...prev, name: e.target.value }))}
                      className="w-full rounded-lg border border-gray-300 px-3 py-2"
                    />
                  </div>

                  <div>
                    <label className="mb-1 block text-sm font-medium text-gray-700">Νομική Επωνυμία</label>
                    <input
                      type="text"
                      value={editForm.legal_name}
                      onChange={(e) =>
                        setEditForm((prev) => ({ ...prev, legal_name: e.target.value }))
                      }
                      className="w-full rounded-lg border border-gray-300 px-3 py-2"
                    />
                  </div>

                  <div>
                    <label className="mb-1 block text-sm font-medium text-gray-700">Email</label>
                    <input
                      type="email"
                      value={editForm.email}
                      onChange={(e) => setEditForm((prev) => ({ ...prev, email: e.target.value }))}
                      className="w-full rounded-lg border border-gray-300 px-3 py-2"
                    />
                  </div>

                  <div>
                    <label className="mb-1 block text-sm font-medium text-gray-700">Τηλέφωνο</label>
                    <input
                      type="text"
                      value={editForm.phone}
                      onChange={(e) => setEditForm((prev) => ({ ...prev, phone: e.target.value }))}
                      className="w-full rounded-lg border border-gray-300 px-3 py-2"
                    />
                  </div>

                  <div>
                    <label className="mb-1 block text-sm font-medium text-gray-700">Website</label>
                    <input
                      type="text"
                      value={editForm.website}
                      onChange={(e) => setEditForm((prev) => ({ ...prev, website: e.target.value }))}
                      className="w-full rounded-lg border border-gray-300 px-3 py-2"
                    />
                  </div>

                  <div>
                    <label className="mb-1 block text-sm font-medium text-gray-700">ΑΦΜ</label>
                    <input
                      type="text"
                      value={editForm.vat_number}
                      onChange={(e) =>
                        setEditForm((prev) => ({ ...prev, vat_number: e.target.value }))
                      }
                      className="w-full rounded-lg border border-gray-300 px-3 py-2"
                    />
                  </div>

                  <div>
                    <label className="mb-1 block text-sm font-medium text-gray-700">Πόλη</label>
                    <input
                      type="text"
                      value={editForm.city}
                      onChange={(e) => setEditForm((prev) => ({ ...prev, city: e.target.value }))}
                      className="w-full rounded-lg border border-gray-300 px-3 py-2"
                    />
                  </div>

                  <div>
                    <label className="mb-1 block text-sm font-medium text-gray-700">Χώρα</label>
                    <input
                      type="text"
                      value={editForm.country}
                      onChange={(e) =>
                        setEditForm((prev) => ({ ...prev, country: e.target.value }))
                      }
                      className="w-full rounded-lg border border-gray-300 px-3 py-2"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="mb-1 block text-sm font-medium text-gray-700">Διεύθυνση</label>
                    <input
                      type="text"
                      value={editForm.address}
                      onChange={(e) =>
                        setEditForm((prev) => ({ ...prev, address: e.target.value }))
                      }
                      className="w-full rounded-lg border border-gray-300 px-3 py-2"
                    />
                  </div>

                  <div>
                    <label className="mb-1 block text-sm font-medium text-gray-700">Κατάσταση</label>
                    <select
                      value={editForm.status}
                      onChange={(e) => setEditForm((prev) => ({ ...prev, status: e.target.value }))}
                      className="w-full rounded-lg border border-gray-300 px-3 py-2"
                    >
                      {statusOptions
                        .filter((x) => x.value !== 'all')
                        .map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                    </select>
                  </div>

                  <div className="md:col-span-2">
                    <label className="mb-1 block text-sm font-medium text-gray-700">Σημειώσεις</label>
                    <textarea
                      rows={3}
                      value={editForm.notes}
                      onChange={(e) => setEditForm((prev) => ({ ...prev, notes: e.target.value }))}
                      className="w-full rounded-lg border border-gray-300 px-3 py-2"
                    />
                  </div>

                  <div className="md:col-span-2 flex justify-end gap-3">
                    <button
                      type="button"
                      onClick={() => setOverviewEditing(false)}
                      className="rounded-lg bg-gray-100 px-4 py-2 text-gray-700 hover:bg-gray-200"
                    >
                      Ακύρωση
                    </button>
                    <button
                      type="submit"
                      className="rounded-lg bg-blue-600 px-4 py-2 text-white hover:bg-blue-700"
                    >
                      Αποθήκευση
                    </button>
                  </div>
                </form>
              ) : (
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <InfoCard label="Όνομα" value={selectedClient.name} />
                  <InfoCard label="Νομική Επωνυμία" value={selectedClient.legal_name} />
                  <InfoCard label="Email" value={selectedClient.email} />
                  <InfoCard label="Τηλέφωνο" value={selectedClient.phone} />
                  <InfoCard label="Website" value={selectedClient.website} />
                  <InfoCard label="ΑΦΜ" value={selectedClient.vat_number} />
                  <InfoCard label="Πόλη" value={selectedClient.city} />
                  <InfoCard label="Χώρα" value={selectedClient.country} />
                  <InfoCard label="Κύρια Επαφή" value={selectedClient.primary_contact_name} />
                  <InfoCard label="Email Επαφής" value={selectedClient.primary_contact_email} />
                  <InfoCard label="Τηλέφωνο Επαφής" value={selectedClient.primary_contact_phone} />
                  <InfoCard label="Θέση Επαφής" value={selectedClient.primary_contact_position} />
                  <div className="md:col-span-2">
                    <InfoCard label="Διεύθυνση" value={selectedClient.address} />
                  </div>
                  <div className="md:col-span-2">
                    <InfoCard label="Σημειώσεις" value={selectedClient.notes} />
                  </div>
                </div>
              )}
            </div>

            <div className="rounded-xl border border-gray-200 p-5">
              <div className="mb-4 flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Portal Access</h3>
                  <p className="text-sm text-gray-500">Διαχείριση πρόσβασης πελάτη στο portal.</p>
                </div>
              </div>

              {portalRows.length === 0 ? (
                <p className="text-sm text-gray-500">Δεν υπάρχουν επαφές για portal access.</p>
              ) : (
                <div className="space-y-3">
                  {portalRows.map((row) => {
                    const latestInvite = row.portal_access_id
                      ? latestInviteByAccessId[row.portal_access_id]
                      : null

                    return (
                      <div
                        key={row.contact_id || row.portal_access_id || row.contact_email}
                        className="rounded-lg border border-gray-200 p-4"
                      >
                        <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
                          <div className="space-y-1">
                            <div className="font-medium text-gray-900">{row.contact_name || '—'}</div>
                            <div className="text-sm text-gray-600">{row.contact_email || '—'}</div>
                            <div className="flex flex-wrap gap-2 pt-1">
                              <span
                                className={`rounded-full px-2.5 py-1 text-xs font-medium ${
                                  row.portal_access_id
                                    ? portalStatusClasses(!!row.portal_access_active)
                                    : 'bg-gray-100 text-gray-700'
                                }`}
                              >
                                {row.portal_access_id
                                  ? row.portal_access_active ? 'Portal ενεργό' : 'Portal ανενεργό'
                                  : 'Χωρίς portal access'}
                              </span>

                              {row.last_login_at ? (
                                <span className="text-xs text-gray-500">
                                  Last login: {new Date(row.last_login_at).toLocaleString('el-GR')}
                                </span>
                              ) : null}
                            </div>

                            {latestInvite ? (
                              <div className="pt-2 text-xs text-gray-500">
                                Latest invite:{' '}
                                {latestInvite.created_at
                                  ? new Date(latestInvite.created_at).toLocaleString('el-GR')
                                  : '—'}
                                {' · '}
                                {latestInvite.used_at ? 'Used' : 'Not used'}
                              </div>
                            ) : null}
                          </div>

                          <div className="flex flex-wrap gap-2">
                            {!row.portal_access_id ? (
                              <button
                                onClick={() => handleEnablePortalAccess(row)}
                                disabled={portalActionLoading}
                                className="rounded-lg bg-blue-600 px-3 py-2 text-sm text-white hover:bg-blue-700 disabled:opacity-50"
                              >
                                Ενεργοποίηση Portal
                              </button>
                            ) : (
                              <>
                                <button
                                  onClick={() => handleCreateInvite(row.portal_access_id)}
                                  disabled={portalActionLoading || !row.portal_access_active}
                                  className="rounded-lg bg-blue-600 px-3 py-2 text-sm text-white hover:bg-blue-700 disabled:opacity-50"
                                >
                                  Generate Invite
                                </button>

                                {latestInvite?.invite_token ? (
                                  <button
                                    onClick={() => handleCopyInvite(latestInvite.invite_token)}
                                    disabled={portalActionLoading}
                                    className="rounded-lg bg-gray-100 px-3 py-2 text-sm text-gray-700 hover:bg-gray-200 disabled:opacity-50"
                                  >
                                    Copy Latest Invite
                                  </button>
                                ) : null}

                                {row.portal_access_active ? (
                                  <button
                                    onClick={() => handleTogglePortalAccess(row.portal_access_id, false)}
                                    disabled={portalActionLoading}
                                    className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700 hover:bg-red-100 disabled:opacity-50"
                                  >
                                    Απενεργοποίηση
                                  </button>
                                ) : (
                                  <button
                                    onClick={() => handleTogglePortalAccess(row.portal_access_id, true)}
                                    disabled={portalActionLoading}
                                    className="rounded-lg bg-green-50 px-3 py-2 text-sm text-green-700 hover:bg-green-100 disabled:opacity-50"
                                  >
                                    Επανενεργοποίηση
                                  </button>
                                )}
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}
            </div>

            <div className="rounded-xl border border-gray-200 p-5">
              <div className="mb-4 flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Portal Invites</h3>
                  <p className="text-sm text-gray-500">Ιστορικό invite links για τον πελάτη.</p>
                </div>
              </div>

              {clientInvites.length === 0 ? (
                <p className="text-sm text-gray-500">Δεν υπάρχουν invites.</p>
              ) : (
                <div className="space-y-3">
                  {clientInvites.map((invite) => (
                    <div
                      key={invite.invite_id}
                      className="flex flex-col gap-3 rounded-lg border border-gray-200 p-4 md:flex-row md:items-center md:justify-between"
                    >
                      <div>
                        <div className="font-medium text-gray-900">{invite.email || '—'}</div>
                        <div className="text-xs text-gray-500">
                          Created: {invite.created_at ? new Date(invite.created_at).toLocaleString('el-GR') : '—'}
                        </div>
                        <div className="text-xs text-gray-500">
                          Expires: {invite.expires_at ? new Date(invite.expires_at).toLocaleString('el-GR') : '—'}
                        </div>
                        <div className="text-xs text-gray-500">
                          {invite.used_at
                            ? `Used: ${new Date(invite.used_at).toLocaleString('el-GR')}`
                            : 'Not used yet'}
                        </div>
                      </div>

                      <button
                        onClick={() => handleCopyInvite(invite.invite_token)}
                        disabled={portalActionLoading}
                        className="rounded-lg bg-gray-100 px-3 py-2 text-sm text-gray-700 hover:bg-gray-200 disabled:opacity-50"
                      >
                        Copy Link
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="rounded-xl border border-gray-200 p-5">
              <div className="mb-4 flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Portal Sessions</h3>
                  <p className="text-sm text-gray-500">Ενεργά και revoked sessions του πελάτη.</p>
                </div>
              </div>

              {clientSessions.length === 0 ? (
                <p className="text-sm text-gray-500">Δεν υπάρχουν sessions.</p>
              ) : (
                <div className="space-y-3">
                  {clientSessions.map((session) => (
                    <div
                      key={session.session_id}
                      className="flex flex-col gap-3 rounded-lg border border-gray-200 p-4 md:flex-row md:items-center md:justify-between"
                    >
                      <div>
                        <div className="font-medium text-gray-900">{session.email || '—'}</div>
                        <div className="flex flex-wrap gap-2 pt-1">
                          <span
                            className={`rounded-full px-2.5 py-1 text-xs font-medium ${sessionStatusClasses(
                              session.revoked_at,
                              session.expires_at
                            )}`}
                          >
                            {session.revokedAt ? 'Revoked' : 'Active'}
                          </span>
                        </div>
                        <div className="text-xs text-gray-500">
                          Created: {session.created_at ? new Date(session.created_at).toLocaleString('el-GR') : '—'}
                        </div>
                        <div className="text-xs text-gray-500">
                          Expires: {session.expires_at ? new Date(session.expires_at).toLocaleString('el-GR') : '—'}
                        </div>
                        {session.last_seen_at ? (
                          <div className="text-xs text-gray-500">
                            Last seen: {new Date(session.last_seen_at).toLocaleString('el-GR')}
                          </div>
                        ) : null}
                      </div>

                      {!session.revoked_at ? (
                        <button
                          onClick={() => handleRevokeSession(session.session_id)}
                          disabled={portalActionLoading}
                          className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700 hover:bg-red-100 disabled:opacity-50"
                        >
                          Revoke Session
                        </button>
                      ) : null}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="rounded-xl border border-gray-200 p-5">
              <div className="mb-4 flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Επαφές</h3>
                  <p className="text-sm text-gray-500">Όλες οι επαφές του πελάτη.</p>
                </div>

                <button
                  onClick={openAddContactModal}
                  className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-3 py-2 text-sm text-white hover:bg-blue-700"
                >
                  <SafeIcon icon={FiIcons.FiPlus} className="h-4 w-4" />
                  Νέα Επαφή
                </button>
              </div>

              {clientContacts.length === 0 ? (
                <p className="text-sm text-gray-500">Δεν υπάρχουν επαφές.</p>
              ) : (
                <div className="space-y-3">
                  {clientContacts.map((contact) => (
                    <div
                      key={contact.id}
                      className="flex flex-col gap-3 rounded-lg border border-gray-200 p-4 md:flex-row md:items-center md:justify-between"
                    >
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-gray-900">{contact.full_name}</span>
                          {contact.is_primary ? (
                            <span className="rounded-full bg-green-100 px-2 py-0.5 text-xs text-green-700">
                              Κύρια
                            </span>
                          ) : null}
                        </div>
                        <div className="mt-1 text-sm text-gray-600">
                          <div>{contact.email || '—'}</div>
                          <div>{contact.phone || '—'}</div>
                          <div>{contact.contact_position || '—'}</div>
                        </div>
                      </div>

                      {!contact.is_primary ? (
                        <button
                          onClick={() => handleSetPrimaryContact(contact.id)}
                          className="inline-flex items-center gap-2 rounded-lg bg-gray-100 px-3 py-2 text-sm text-gray-700 hover:bg-gray-200"
                        >
                          <SafeIcon icon={FiIcons.FiStar} className="h-4 w-4" />
                          Ορισμός ως Κύρια
                        </button>
                      ) : null}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="rounded-xl border border-gray-200 p-5">
              <div className="mb-4 flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Εσωτερικά Έργα</h3>
                  <p className="text-sm text-gray-500">Projects που ανήκουν στον πελάτη.</p>
                </div>

                <button
                  onClick={() => navigate('/admin/internal-projects')}
                  className="inline-flex items-center gap-2 rounded-lg bg-purple-600 px-3 py-2 text-sm text-white hover:bg-purple-700"
                >
                  <SafeIcon icon={FiIcons.FiBriefcase} className="h-4 w-4" />
                  Μετάβαση στα Projects
                </button>
              </div>

              {clientProjects.length === 0 ? (
                <p className="text-sm text-gray-500">Δεν υπάρχουν εσωτερικά έργα για αυτόν τον πελάτη.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">Τίτλος</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">Lead</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">Κατάσταση</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">Προτεραιότητα</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">Ημ/νία</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 bg-white">
                      {clientProjects.map((project) => (
                        <tr key={project.id}>
                          <td className="px-4 py-3 text-sm text-gray-900">{project.title}</td>
                          <td className="px-4 py-3 text-sm text-gray-700">{project.lead_name || '—'}</td>
                          <td className="px-4 py-3 text-sm text-gray-700">{project.status || '—'}</td>
                          <td className="px-4 py-3 text-sm text-gray-700">{project.priority || '—'}</td>
                          <td className="px-4 py-3 text-sm text-gray-700">
                            {project.created_at ? new Date(project.created_at).toLocaleString('el-GR') : '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}
      </Modal>

      <Modal
        isOpen={isContactModalOpen}
        onClose={() => setIsContactModalOpen(false)}
        title="Νέα Επαφή"
        size="md"
      >
        <form onSubmit={handleAddContact} className="space-y-4">
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Ονοματεπώνυμο *</label>
            <input
              type="text"
              required
              value={contactForm.full_name}
              onChange={(e) => setContactForm((prev) => ({ ...prev, full_name: e.target.value }))}
              className="w-full rounded-lg border border-gray-300 px-3 py-2"
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Email</label>
            <input
              type="email"
              value={contactForm.email}
              onChange={(e) => setContactForm((prev) => ({ ...prev, email: e.target.value }))}
              className="w-full rounded-lg border border-gray-300 px-3 py-2"
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Τηλέφωνο</label>
            <input
              type="text"
              value={contactForm.phone}
              onChange={(e) => setContactForm((prev) => ({ ...prev, phone: e.target.value }))}
              className="w-full rounded-lg border border-gray-300 px-3 py-2"
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Θέση</label>
            <input
              type="text"
              value={contactForm.position}
              onChange={(e) => setContactForm((prev) => ({ ...prev, position: e.target.value }))}
              className="w-full rounded-lg border border-gray-300 px-3 py-2"
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Σημειώσεις</label>
            <textarea
              rows={3}
              value={contactForm.notes}
              onChange={(e) => setContactForm((prev) => ({ ...prev, notes: e.target.value }))}
              className="w-full rounded-lg border border-gray-300 px-3 py-2"
            />
          </div>

          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={contactForm.make_primary}
              onChange={(e) => setContactForm((prev) => ({ ...prev, make_primary: e.target.checked }))}
            />
            Ορισμός ως κύρια επαφή
          </label>

          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={contactForm.portal_enabled}
              onChange={(e) => setContactForm((prev) => ({ ...prev, portal_enabled: e.target.checked }))}
            />
            Ενεργοποίηση portal later
          </label>

          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={() => setIsContactModalOpen(false)}
              className="rounded-lg bg-gray-100 px-4 py-2 text-gray-700 hover:bg-gray-200"
            >
              Ακύρωση
            </button>

            <button
              type="submit"
              className="rounded-lg bg-blue-600 px-4 py-2 text-white hover:bg-blue-700"
            >
              Αποθήκευση
            </button>
          </div>
        </form>
      </Modal>
    </div>
  )
}

function InfoCard({ label, value }) {
  return (
    <div className="rounded-lg bg-gray-50 p-4">
      <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">{label}</div>
      <div className="mt-1 whitespace-pre-wrap text-sm text-gray-900">{value || '—'}</div>
    </div>
  )
}